import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";

const SuperAdminContext = createContext(null);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function SuperAdminProvider({ children }) {
  const [admin, setAdmin] = useState(null);
  // In-memory only — auth persists via httpOnly cookie (admin_access_token)
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const api = useCallback(() => {
    const instance = axios.create({
      baseURL: API_URL,
      withCredentials: true,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return instance;
  }, [token]);

  useEffect(() => {
    // Only the /platform area has a superadmin session — tenant pages must not probe /admin/me on every load
    if (!window.location.pathname.startsWith("/platform") && !token) {
      setAdmin(null);
      setLoading(false);
      return;
    }
    api()
      .get("/admin/me")
      .then((res) => {
        setAdmin(res.data);
        setLoading(false);
      })
      .catch(() => {
        setAdmin(null);
        setLoading(false);
      });
  }, [token, api]);

  const apiInstance = useMemo(() => api(), [api]);

  const login = async (email, password) => {
    const res = await axios.post(`${API_URL}/admin/login`, { email, password }, { withCredentials: true });
    localStorage.removeItem("superadmin_token");
    setToken(res.data.token);
    setAdmin(res.data.admin);
    return res.data;
  };

  const logout = async () => {
    try { await axios.post(`${API_URL}/admin/logout`, {}, { withCredentials: true }); } catch { /* */ }
    localStorage.removeItem("superadmin_token");
    setToken(null);
    setAdmin(null);
  };

  return (
    <SuperAdminContext.Provider value={{ admin, token, loading, login, logout, api: apiInstance }}>
      {children}
    </SuperAdminContext.Provider>
  );
}

export function useSuperAdmin() {
  const ctx = useContext(SuperAdminContext);
  if (!ctx) throw new Error("useSuperAdmin must be used within SuperAdminProvider");
  return ctx;
}
